import { kml } from '@tmcw/togeojson'
import type { FeatureCollection, Geometry } from 'geojson'
import JSZip from 'jszip'

type LoadedKml = {
	geoJson: FeatureCollection<Geometry | null, Record<string, unknown> | null>
	dispose: () => void
}

const resolveUrl = (path: string, baseUrl: string): string => {
	const url = new URL(path, baseUrl)
	if (url.protocol !== 'https:' && url.protocol !== 'http:') {
		throw new Error('Unsupported KML resource protocol')
	}
	return url.href
}

export const loadKml = async (url: string, signal: AbortSignal): Promise<LoadedKml> => {
	const response = await fetch(url, { signal })
	if (!response.ok) throw new Error(`KML download failed (HTTP ${response.status})`)

	const bytes = new Uint8Array(await response.arrayBuffer())
	const objectUrls: string[] = []
	const resourceUrls = new Map<string, string>()
	const dispose = () => objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl))

	try {
		let text: string
		let archive: JSZip | undefined
		let documentPath = ''
		if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
			archive = await JSZip.loadAsync(bytes)
			const documents = Object.values(archive.files)
				.filter((file) => !file.dir && /\.kml$/i.test(file.name))
				.sort((a, b) => a.name.localeCompare(b.name))
			const document = archive.file(/^doc\.kml$/i)[0] || documents[0]
			if (!document) throw new Error('KMZ archive does not contain a KML document')
			documentPath = document.name
			text = await document.async('string')
		} else {
			text = new TextDecoder().decode(bytes)
		}

		const xml = new DOMParser().parseFromString(text, 'application/xml')
		if (xml.querySelector('parsererror') || xml.documentElement.localName !== 'kml') {
			throw new Error('Invalid KML document')
		}
		if (xml.getElementsByTagNameNS('*', 'NetworkLink').length) {
			throw new Error('KML NetworkLink is not supported; upload a file containing the geometries')
		}
		if (xml.getElementsByTagNameNS('*', 'GroundOverlay').length) {
			throw new Error('KML GroundOverlay is not supported; upload vector geometries')
		}

		const geoJson: LoadedKml['geoJson'] = kml(xml, { skipNullGeometry: true })
		if (!geoJson.features.length) throw new Error('KML document contains no supported geometries')

		for (const feature of geoJson.features) {
			const icon: unknown = feature.properties?.icon
			if (typeof icon !== 'string' || !icon) continue

			// Resolve KMZ icons relative to its main document, not the application URL.
			const archivePath = new URL(icon, `https://kmz.invalid/${documentPath}`).pathname.slice(1)
			const resource = archive?.file(decodeURIComponent(archivePath))
			if (resource && !/^[a-z][a-z\d+.-]*:|^\/\//i.test(icon)) {
				const cachedUrl = resourceUrls.get(resource.name)
				if (cachedUrl && feature.properties) {
					feature.properties.icon = cachedUrl
					continue
				}
				const extension = resource.name.split('.').pop()?.toLowerCase()
				const mimeType =
					extension === 'svg'
						? 'image/svg+xml'
						: extension === 'jpg' || extension === 'jpeg'
						? 'image/jpeg'
						: 'image/png'
				const data = await resource.async('arraybuffer')
				const objectUrl = URL.createObjectURL(new Blob([data], { type: mimeType }))
				objectUrls.push(objectUrl)
				resourceUrls.set(resource.name, objectUrl)
				if (feature.properties) feature.properties.icon = objectUrl
			} else if (feature.properties) {
				feature.properties.icon = resolveUrl(icon, response.url || url)
			}
		}

		return { geoJson, dispose }
	} catch (error) {
		dispose()
		throw error
	}
}
